// src/components/contacts/WebsitePreviewInline.tsx
//
// Renders a small Thum.io-backed thumbnail directly under the website
// row in the Contact Workspace Website Intelligence section. The image
// is fetched from Outbound's `WebsitePreviewService` via the CRM proxy
// at `/api/outbound/contact-workspace/website-preview?url=<encoded>`.
//
// AUTH: the CRM API authenticates with an `Authorization: Bearer <token>`
// header (see `baseApi.ts` prepareHeaders + the server's `requireHybridAuth`,
// which has NO cookie/query fallback). A plain `<img src=proxy>` cannot set
// that header — it only sends cookies — so the request would 401 and the
// preview would always fail. Instead we fetch the bytes through an
// authenticated `fetch` (same token resolution as `baseApi`), turn the
// response into a Blob, and render an object-URL `<img>`. NO iframe.
//
// States:
//   • no URL          → renders nothing (skip)
//   • loading         → dashed skeleton box
//   • error / no bytes → subtle "Could not load website preview." line
//   • ok              → clickable <img> that opens the website in a new tab

import { useEffect, useState } from 'react';
import { useAppSelector } from '../../store/store';

interface Props {
  websiteUrl: string | null;
}

function buildPreviewUrl(url: string): string {
  return `/api/outbound/contact-workspace/website-preview?url=${encodeURIComponent(url)}`;
}

export default function WebsitePreviewInline({ websiteUrl }: Props) {
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  // Same token resolution order as `baseApi.ts`: prefer the Central access
  // token, fall back to the legacy auth token.
  const token = useAppSelector(
    (s) => s.centralAuth?.accessToken ?? s.auth?.token ?? null,
  );

  useEffect(() => {
    if (!websiteUrl) {
      setState('loading');
      setObjectUrl(null);
      return;
    }

    setState('loading');
    const ctrl = new AbortController();
    let createdUrl: string | null = null;

    (async () => {
      try {
        const res = await fetch(buildPreviewUrl(websiteUrl), {
          method: 'GET',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: ctrl.signal,
        });
        if (!res.ok) {
          setState('error');
          return;
        }
        const blob = await res.blob();
        if (ctrl.signal.aborted) return;
        // Guard against an empty / non-image body slipping through.
        if (blob.size === 0) {
          setState('error');
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
        setState('ok');
      } catch {
        if (!ctrl.signal.aborted) setState('error');
      }
    })();

    return () => {
      ctrl.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [websiteUrl, token]);

  if (!websiteUrl) return null;

  return (
    <div data-testid="cw-website-preview" className="mt-2">
      {state === 'loading' ? (
        <div
          data-testid="cw-website-preview-loading"
          className="flex h-32 w-full max-w-sm items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-500"
        >
          Loading website preview…
        </div>
      ) : null}
      {state === 'error' ? (
        <p
          data-testid="cw-website-preview-error"
          className="text-xs italic text-slate-500"
        >
          Could not load website preview.
        </p>
      ) : null}
      {state === 'ok' && objectUrl ? (
        <a
          href={websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cw-website-preview-link"
          className="inline-block"
        >
          <img
            data-testid="cw-website-preview-image"
            src={objectUrl}
            alt={`Thumbnail of ${websiteUrl}`}
            className="max-h-48 w-auto rounded border border-slate-200 shadow-sm transition hover:shadow"
          />
        </a>
      ) : null}
    </div>
  );
}
